"use client";

/**
 * An event's photos and videos -- its own, its programme's and its trails' --
 * each opening the gallery at itself, a video marked with a play button. Two shapes: a mosaic of two rows scrolled sideways on
 * a phone, under the banner, holding every one of them; a column of three
 * stacked beside the banner on a desktop, whose last tile says how many more
 * there are. A thumbnail is fetched as its tile comes near the screen, so a
 * long strip costs nothing until it is swiped.
 */
import { Images, Play } from "lucide-react";
import { track } from "@/lib/analytics";
import { cdnUrl } from "@/lib/cdn";
import type { Photo } from "@/lib/types/event";
import { openGallery } from "@/components/event/EventGallery";

const COLUMN_MAX = 3;

function Tile({ photo, index, photos, eventId, title, more, className = "" }: { photo: Photo; index: number; photos: Photo[]; eventId: number | string; title: string; more: number; className?: string }) {
  return (
    <button
      type="button"
      className={`pressable relative snap-start overflow-hidden rounded-[12px] bg-line ${className}`}
      aria-label={photo.video ? `Vídeo ${index + 1}` : `Fotografia ${index + 1}`}
      onClick={() => {
        track("view_photos", { item_id: String(eventId), where: "evento", ...(photo.video ? { video: "1" } : {}) });
        openGallery(photos, index);
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- a thumbnail straight off the CDN */}
      <img src={cdnUrl(photo.thumbnail || photo.path)} alt={`${title}: ${photo.video ? "vídeo" : "fotografia"} ${index + 1}`} loading="lazy" decoding="async" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      {!!photo.video && more === 0 && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm">
            <Play size={17} fill="currentColor" className="translate-x-px" />
          </span>
        </span>
      )}
      {more > 0 && (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 text-white">
          <Images size={20} />
          <span className="text-[13px] font-bold">+{more}</span>
        </span>
      )}
    </button>
  );
}

export function EventPhotos({ photos, eventId, title = "", variant = "mosaic", className = "" }: { photos: Photo[] | null | undefined; eventId: number | string; /** the event's, for each photo's alt text */ title?: string | null; variant?: "mosaic" | "column"; className?: string }) {
  const name = String(title || "").trim() || "Evento";
  const list = photos || [];
  if (list.length === 0) {
    return null;
  }

  if (variant === "column") {
    const shown = list.slice(0, COLUMN_MAX);
    const rest = list.length - shown.length;
    return (
      <div className={`grid h-full grid-rows-3 gap-2 ${className}`}>
        {shown.map((photo, i) => (
          <Tile key={photo.id} photo={photo} index={i} photos={list} eventId={eventId} title={name} more={i === shown.length - 1 ? rest : 0} className="min-h-0" />
        ))}
      </div>
    );
  }
  // one row when there are too few for two
  const rows = list.length >= 4 ? 2 : 1;
  return (
    <div className={`scroll-x -mx-4 snap-x px-4 ${className}`}>
      <div className={`grid grid-flow-col gap-1.5 ${rows === 2 ? "grid-rows-2 auto-cols-[38%]" : "grid-rows-1 auto-cols-[46%]"}`}>
        {list.map((photo, i) => (
          <Tile key={photo.id} photo={photo} index={i} photos={list} eventId={eventId} title={name} more={0} className="aspect-square" />
        ))}
      </div>
    </div>
  );
}
